import './ScheduleField.css';

import { date, isNonNullable, isNullable } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import FormErrorMessage from 'components/FormErrorMessage';
import { useLocale } from 'containers/App/LocaleContext';
import { List } from 'immutable';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { map, pipe, prepend } from 'ramda';
import { PureComponent } from 'react';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import Select, { components } from 'react-select';
import handleEvent from 'utils/handleEvent';
import { isApple } from 'utils/platform/isApple';
import getValue from './getValue';

const MultiValueMenu = (props) => {
  const msg = useLocale();
  return (
    <components.Menu {...props}>
      {props.children}
      <div className='schedule-field__menu-help text-lgu-lg px-lgu-3 py-lgu-1'>
        {isApple
          ? msg.scheduleField.multiSelectHelpMac
          : msg.scheduleField.multiSelectHelp}
      </div>
    </components.Menu>
  );
};

MultiValueMenu.propTypes = {
  children: PropTypes.any.isRequired,
};

export default class ScheduleField extends PureComponent {
  static propTypes = {
    disabled: PropTypes.bool,
    input: PropTypes.object.isRequired,
    meta: PropTypes.object,
    msg: PropTypes.object.isRequired,
    disableMinutes: PropTypes.bool,
    disableHour: PropTypes.bool,
    disableDay: PropTypes.bool,
    disableWeek: PropTypes.bool,
    disableMonth: PropTypes.bool,
    disableYear: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    const inputValue = get(props, ['input', 'value']);
    if (!inputValue) {
      this.state = {
        period: 'minute',
        months: null,
        days: null,
        daysOfWeek: null,
        hours: null,
        minutes: null,
        minuteField: 5,
        value: '',
        timezone: 'UTC',
      };
    } else {
      this.state = this.parseValue(inputValue);
    }
  }

  componentDidMount() {
    document.body.addEventListener('keydown', this.handleKeyDown);
    document.body.addEventListener('keyup', this.handleKeyUp);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (
      this.props.input.value.expression !== prevState.value ||
      this.props.input.value.timezone !== prevState.timezone
    ) {
      this.setState(this.parseValue(this.props.input.value));
    }
  }

  componentWillUnmount() {
    document.body.removeEventListener('keydown', this.handleKeyDown);
    document.body.removeEventListener('keyup', this.handleKeyUp);
  }

  timezoneOptions = map((name) => ({ value: name, label: name }))(
    date.timezones,
  );

  getMinutesComponent = () => {
    const { msg, disabled } = this.props;
    const { minutes } = this.state;
    const sortMinutes =
      minutes !== '' && isNonNullable(minutes)
        ? minutes
            .toString()
            .split(',')
            .map((v) => Number(v))
            .sort((a, b) => a - b)
        : [];
    const minutesOptions = this.getMinuteOptions();
    return (
      <Select
        className='minutes-select'
        classNamePrefix='react-select'
        closeMenuOnSelect={false}
        components={{
          Menu: MultiValueMenu,
        }}
        hideSelectedOptions={false}
        isDisabled={disabled}
        isMulti
        onChange={this.handleMinutesChange}
        options={minutesOptions}
        placeholder={msg.selectMinutes}
        isSearchable={false}
        styles={{
          clearIndicator: (base) => ({
            ...base,
            padding: '2px 0 2px 8px',
            marginLeft: 10,
          }),
          multiValue: (base) => ({ ...base, paddingRight: 3 }),
          multiValueRemove: (base) => ({ ...base, display: 'none' }),
          control: (base) => ({ ...base, minHeight: 30, borderRadius: 0 }),
          dropdownIndicator: (base) => ({ ...base, padding: '2px 8px' }),
          valueContainer: (base) => ({ ...base, padding: '0 6px' }),
          option: (base) => ({
            ...base,
            float: 'left',
            paddingLeft: 0,
            paddingRight: 0,
            textAlign: 'center',
            width: '25%',
          }),
        }}
        value={minutesOptions.filter((option) =>
          sortMinutes.includes(option.value),
        )}
      />
    );
  };

  getHoursComponent = () => {
    const { msg, disabled } = this.props;
    const { hours } = this.state;
    const sortHours =
      hours !== '' && isNonNullable(hours)
        ? hours
            .toString()
            .split(',')
            .map((v) => Number(v))
            .sort((a, b) => a - b)
        : [];
    const hoursOptions = this.getHoursOptions();
    return (
      <Select
        className='hours-select'
        classNamePrefix='react-select'
        closeMenuOnSelect={false}
        components={{
          Menu: MultiValueMenu,
        }}
        hideSelectedOptions={false}
        isDisabled={disabled}
        isMulti
        onChange={this.handleHoursChange}
        options={hoursOptions}
        placeholder={msg.everyDropdown}
        isSearchable={false}
        styles={{
          clearIndicator: (base) => ({
            ...base,
            padding: '2px 0 2px 8px',
            marginLeft: 10,
          }),
          multiValue: (base) => ({ ...base, paddingRight: 3 }),
          multiValueRemove: (base) => ({ ...base, display: 'none' }),
          control: (base) => ({ ...base, minHeight: 30, borderRadius: 0 }),
          dropdownIndicator: (base) => ({ ...base, padding: '2px 8px' }),
          valueContainer: (base) => ({ ...base, padding: '0 6px' }),
          option: (base) => ({
            ...base,
            float: 'left',
            paddingLeft: 0,
            paddingRight: 0,
            textAlign: 'center',
            width: '25%',
          }),
        }}
        value={hoursOptions.filter((option) =>
          sortHours.includes(option.value),
        )}
      />
    );
  };

  getDaysOfWeekComponent = () => {
    const { msg, disabled } = this.props;
    const { daysOfWeek } = this.state;
    const daysOfWeekOptions = this.getDaysOfWeekOptions(msg);
    return (
      <Select
        className='days-of-week-select'
        classNamePrefix='react-select'
        closeMenuOnSelect={false}
        components={{
          Menu: MultiValueMenu,
        }}
        hideSelectedOptions={false}
        isDisabled={disabled}
        isMulti
        isSearchable={false}
        onChange={this.handleDaysOfWeekChange}
        options={daysOfWeekOptions}
        placeholder={msg.everyDayOfWeek}
        styles={{
          clearIndicator: (base) => ({
            ...base,
            padding: '2px 0 2px 8px',
            marginLeft: 10,
          }),
          multiValue: (base) => ({ ...base, paddingRight: 3 }),
          multiValueRemove: (base) => ({ ...base, display: 'none' }),
          control: (base) => ({ ...base, minHeight: 30, borderRadius: 0 }),
          dropdownIndicator: (base) => ({ ...base, padding: '2px 8px' }),
          valueContainer: (base) => ({ ...base, padding: '0 6px' }),
        }}
        value={
          daysOfWeek &&
          daysOfWeekOptions.filter((option) =>
            daysOfWeek.includes(option.value),
          )
        }
      />
    );
  };

  getDaysComponent = () => {
    const { msg, disabled } = this.props;
    const { days } = this.state;
    const sortDays =
      days !== '' && isNonNullable(days)
        ? days
            .toString()
            .split(',')
            .map((v) => Number(v))
            .sort((a, b) => a - b)
        : [];
    const daysOptions = this.getDaysOptions();
    return (
      <Select
        className='days-select'
        classNamePrefix='react-select'
        closeMenuOnSelect={false}
        components={{
          Menu: MultiValueMenu,
        }}
        hideSelectedOptions={false}
        isDisabled={disabled}
        isMulti
        onChange={this.handleDaysChange}
        options={daysOptions}
        placeholder={msg.everyDayOfMonth}
        isSearchable={false}
        styles={{
          clearIndicator: (base) => ({
            ...base,
            padding: '2px 0 2px 8px',
            marginLeft: 10,
          }),
          multiValue: (base) => ({ ...base, paddingRight: 3 }),
          multiValueRemove: (base) => ({ ...base, display: 'none' }),
          control: (base) => ({ ...base, minHeight: 30, borderRadius: 0 }),
          dropdownIndicator: (base) => ({ ...base, padding: '2px 8px' }),
          valueContainer: (base) => ({ ...base, padding: '0 6px' }),
          option: (base) => ({
            ...base,
            float: 'left',
            paddingLeft: 0,
            paddingRight: 0,
            textAlign: 'center',
            width: '25%',
          }),
        }}
        value={daysOptions.filter((option) => sortDays.includes(option.value))}
      />
    );
  };

  getMonthsComponent = () => {
    const { msg, disabled } = this.props;
    const { months } = this.state;
    const sortMonths =
      months !== '' && isNonNullable(months)
        ? months
            .toString()
            .split(',')
            .map((v) => Number(v))
            .sort((a, b) => a - b)
        : [];
    const monthsOptions = this.getMonthsOptions(msg);
    return (
      <Select
        className='months-select'
        classNamePrefix='react-select'
        closeMenuOnSelect={false}
        components={{
          Menu: MultiValueMenu,
        }}
        hideSelectedOptions={false}
        isDisabled={disabled}
        isMulti
        isSearchable={false}
        onChange={this.handleMonthsChange}
        options={monthsOptions}
        placeholder={msg.everyMonth}
        styles={{
          multiValue: (base) => ({ ...base, paddingRight: 3 }),
          multiValueRemove: (base) => ({ ...base, display: 'none' }),
          control: (base) => ({ ...base, minHeight: 30, borderRadius: 0 }),
          dropdownIndicator: (base) => ({ ...base, padding: '2px 8px' }),
          valueContainer: (base) => ({ ...base, padding: '0 6px' }),
        }}
        value={monthsOptions.filter((option) =>
          sortMonths.includes(option.value),
        )}
      />
    );
  };

  getMinute = () => {
    const { msg } = this.props;
    const { minuteField } = this.state;
    return (
      <div className='schedule-field__group'>
        <input
          className='form-control input-sm schedule-field__minute-input'
          max='60'
          min='1'
          onChange={this.handleMinuteFieldChange}
          type='number'
          value={minuteField}
        />
        <span className='schedule-field__word ml-2'>
          <FormattedMessage
            id='scheduleField.minutesField'
            defaultMessage={`{minutesCount, plural, one {${msg.minute}} other {${msg.minutes}}}`}
            values={{ minutesCount: minuteField }}
          />
        </span>
      </div>
    );
  };

  getHour = () => {
    const { msg } = this.props;
    const { minutes } = this.state;
    return (
      <div className='schedule-field__group'>
        <span className='schedule-field__word'>{msg.at}</span>
        {this.getMinutesComponent()}
        <span className='schedule-field__word ml-2'>
          <FormattedMessage
            id='scheduleField.minutesField'
            defaultMessage={`{minutesCount, plural, one {${msg.minute}} other {${msg.minutes}}} past the hour`}
            values={{
              minutesCount:
                minutes === '1' ||
                minutes === '' ||
                minutes === '*' ||
                isNullable(minutes)
                  ? 1
                  : 2,
            }}
          />
        </span>
      </div>
    );
  };

  getDay = () => {
    const { msg } = this.props;
    return (
      <div className='schedule-field__group'>
        <span className='schedule-field__word'>{msg.at}</span>
        {this.getHoursComponent()}
        {this.getHoursText()}
        <span className='mr-2'>:</span>
        {this.getMinutesComponent()}
        {this.getMinutesText()}
        <span className='schedule-field__end-space' />
      </div>
    );
  };

  getWeek = () => {
    const { msg } = this.props;
    return (
      <>
        <div className='schedule-field__group'>
          <span className='schedule-field__word'>{msg.on}</span>
          {this.getDaysOfWeekComponent()}
        </div>
        <div className='schedule-field__group'>
          <span className='schedule-field__word'>{msg.at}</span>
          {this.getHoursComponent()}
          {this.getHoursText()}
          <span className='mr-2'>:</span>
          {this.getMinutesComponent()}
          {this.getMinutesText()}
          <span className='schedule-field__end-space' />
        </div>
      </>
    );
  };

  getMonth = () => {
    const { msg } = this.props;
    return (
      <>
        <div className='schedule-field__group'>
          <span className='schedule-field__word'>{msg.on}</span>
          {this.getDaysComponent()}
        </div>
        <div className='schedule-field__group'>
          <span className='schedule-field__word'>{msg.at}</span>
          {this.getHoursComponent()}
          {this.getHoursText()}
          <span className='mr-2'>:</span>
          {this.getMinutesComponent()}
          {this.getMinutesText()}
          <span className='schedule-field__end-space' />
        </div>
      </>
    );
  };

  getYear = () => {
    const { msg } = this.props;
    return (
      <>
        <div className='schedule-field__group'>
          <span className='schedule-field__word'>{msg.on}</span>
          {this.getDaysComponent()}
        </div>
        <div className='schedule-field__group'>
          <span className='schedule-field__word'>{msg.of}</span>
          {this.getMonthsComponent()}
        </div>
        <div className='schedule-field__group'>
          <span className='schedule-field__word'>{msg.at}</span>
          {this.getHoursComponent()}
          {this.getHoursText()}
          <span className='mr-2'>:</span>
          {this.getMinutesComponent()}
          {this.getMinutesText()}
          <span className='schedule-field__end-space' />
        </div>
      </>
    );
  };

  getTimezone = () => {
    const { disabled, msg } = this.props;
    const { timezone } = this.state;

    return (
      <div className='mb-3 flex flex-row items-center'>
        <span className='mr-2'>{msg.inTimezone}</span>
        <Select
          autosize
          classNamePrefix='react-select'
          hideSelectedOptions={false}
          options={this.timezoneOptions}
          isDisabled={disabled}
          isSearchable
          styles={{
            clearIndicator: (base) => ({
              ...base,
              padding: '2px 0 2px 8px',
              marginLeft: 10,
            }),
            control: (base) => ({
              ...base,
              minHeight: 30,
              borderRadius: 0,
              width: '240px',
            }),
            dropdownIndicator: (base) => ({ ...base, padding: '2px 8px' }),
            multiValue: (base) => ({ ...base, paddingRight: 3 }),
            multiValueRemove: (base) => ({ ...base, display: 'none' }),
            valueContainer: (base) => ({ ...base, padding: '0 6px' }),
          }}
          onChange={this.handleTimezoneChange}
          value={this.timezoneOptions.filter(
            (option) => option.value === timezone,
          )}
        />
      </div>
    );
  };

  getPeriodOptions = () => {
    const {
      msg: { periodOptions },
      disableMinutes,
      disableHour,
      disableDay,
      disableWeek,
      disableMonth,
      disableYear,
    } = this.props;
    return pipe(
      (data) =>
        disableMinutes
          ? data
          : prepend({ value: 'minute', label: periodOptions[0] }, data),
      (data) =>
        disableHour
          ? data
          : data.concat({ value: 'hour', label: periodOptions[1] }),
      (data) =>
        disableDay
          ? data
          : data.concat({ value: 'day', label: periodOptions[2] }),
      (data) =>
        disableWeek
          ? data
          : data.concat({ value: 'week', label: periodOptions[3] }),
      (data) =>
        disableMonth
          ? data
          : data.concat({ value: 'month', label: periodOptions[4] }),
      (data) =>
        disableYear
          ? data
          : data.concat({ value: 'year', label: periodOptions[5] }),
    )([]);
  };

  getMonthsOptions = (msg) =>
    new List()
      .setSize(12)
      .map((_v, i) => ({ value: i + 1, label: msg.months[i] }))
      .toJS();

  getDaysOptions = () =>
    new List()
      .setSize(31)
      .map((_v, i) => ({ value: i + 1, label: i + 1 }))
      .toJS();

  getDaysOfWeekOptions = (msg) =>
    new List()
      .setSize(7)
      .map((_v, i) => ({ value: i + 1, label: msg.daysOfWeek[i] }))
      .toJS();

  getHoursOptions = () =>
    new List()
      .setSize(24)
      .map((_v, i) => ({ value: i, label: i }))
      .toJS();

  getMinuteOptions = () =>
    new List()
      .setSize(60)
      .map((_v, i) => ({ value: i, label: i }))
      .toJS();

  getMinutesText = () => {
    const { minutes } = this.state;
    const { msg } = this.props;
    return (
      <span className='schedule-field__word ml-2'>
        <FormattedPlural
          value={
            minutes === '1' ||
            minutes === '' ||
            minutes === '*' ||
            isNullable(minutes)
              ? 1
              : 2
          }
          one={msg.minute}
          other={msg.minutes}
        />
      </span>
    );
  };

  getHoursText = () => {
    const { hours } = this.state;
    const { msg } = this.props;
    return (
      <span className='schedule-field__word ml-2'>
        <FormattedPlural
          value={
            hours === '1' || hours === '' || hours === '*' || isNullable(hours)
              ? 1
              : 2
          }
          one={msg.hour}
          other={msg.hours}
        />
      </span>
    );
  };

  getDaysOfWeekValue = (formatValue) => {
    switch (formatValue) {
      case 'MON':
        return 1;
      case 'TUE':
        return 2;
      case 'WED':
        return 3;
      case 'THU':
        return 4;
      case 'FRI':
        return 5;
      case 'SAT':
        return 6;
      case 'SUN':
        return 7;
      default:
        return formatValue;
    }
  };

  getErrorMessage = () => {
    const { msg } = this.props;
    const { period, minutes } = this.state;
    if (
      period !== 'minute' &&
      (minutes === '' || isNullable(minutes) || minutes === '*')
    ) {
      return msg.validation.selectMinutes;
    }
    if (period === 'minute') {
      return msg.validation.validMinutes;
    }
    return msg.validation.provideValidValues;
  };

  getSaveValue = (oldValue, value) => {
    const values = (value || []).map((v) => v.value);
    const oldValues = (oldValue || '')
      .split(',')
      .map((v) => Number.parseInt(v, 10));
    if (this.multiPressed) {
      return values.join(',');
    }
    const foundItem = values.find((v) => oldValues.indexOf(v) === -1);
    if (isNonNullable(foundItem)) {
      return foundItem.toString();
    }
    return null;
  };

  parseValue = (inputValue) => ({
    timezone: inputValue.timezone,
    ...this.parseExpressionValue(inputValue.expression),
  });

  parseExpressionValue = (value) => {
    const {
      disableMinutes,
      disableHour,
      disableDay,
      disableWeek,
      disableMonth,
      disableYear,
    } = this.props;
    const str = value
      .replace(/\s+/g, ' ')
      .replace(/^ +/, '')
      .replace(/ +$/, ''); // sanitize
    const mask = str
      .replace(/[^* ]/g, '-')
      .replace(/-+/g, '-')
      .replace(/ +/g, '');
    // const items = str.split(' ').map(v => v === '*' ? '' : v);
    const items = str.split(' ');

    if (items.length !== 5) {
      return {
        period: 'minute',
        months: null,
        days: null,
        daysOfWeek: null,
        hours: null,
        minutes: null,
        minuteField: 5,
        value: '',
      };
    }
    if (mask === '*-****' && !disableMinutes) {
      // 1 possibility
      return {
        period: 'minute',
        months: null,
        days: null,
        daysOfWeek: null,
        hours: null,
        minutes: null,
        minuteField: items[0].split('/')[1],
        value,
      };
    }
    if ((mask === '-****' || mask === '*****') && !disableHour) {
      // 1 possibility
      return {
        period: 'hour',
        months: null,
        days: null,
        daysOfWeek: null,
        hours: null,
        minutes: items[0],
        minuteField: 5,
        value,
      };
    }
    if (mask.substring(2, mask.length) === '***' && !disableDay) {
      // 4 possibilities
      return {
        period: 'day',
        months: null,
        days: null,
        daysOfWeek: null,
        hours: items[1],
        minutes: items[0],
        minuteField: 5,
        value,
      };
    }
    if (mask.substring(2, mask.length) === '-**' && !disableMonth) {
      // 4 possibilities
      return {
        period: 'month',
        months: null,
        days: items[2],
        daysOfWeek: null,
        hours: items[1],
        minutes: items[0],
        minuteField: 5,
        value,
      };
    }
    if (mask.substring(2, mask.length) === '**-' && !disableWeek) {
      // 4 possibilities
      return {
        period: 'week',
        months: null,
        days: null,
        daysOfWeek: items[4]
          .split(',')
          .map((v) => this.getDaysOfWeekValue(v))
          .join(','),
        hours: items[1],
        minutes: items[0],
        minuteField: 5,
        value,
      };
    }
    if (mask.substring(3, mask.length) === '-*' && !disableYear) {
      // 8 possibilities
      return {
        period: 'year',
        months: items[3],
        days: items[2],
        daysOfWeek: null,
        hours: items[1],
        minutes: items[0],
        minuteField: 5,
        value,
      };
    }
    return {
      period: 'minute',
      months: null,
      days: null,
      daysOfWeek: null,
      hours: null,
      minutes: null,
      minuteField: 5,
      value: '',
    };
  };

  handleKeyDown = (e) => {
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      this.multiPressed = true;
    }
  };

  handleKeyUp = () => {
    this.multiPressed = false;
  };

  updateInputValue = () => {
    const invalidMinutes = this.isInvalidMinutes(this.state.minutes);
    const newValue = invalidMinutes ? '' : getValue(this.state);

    this.setState({ value: newValue }, () => {
      if (invalidMinutes) {
        this.props.input.onChange({
          expression: '',
          timezone: this.state.timezone,
        });
      } else {
        this.props.input.onChange({
          expression: newValue,
          timezone: this.state.timezone,
        });
      }
    });
  };

  handleMinuteFieldChange = ({ target: { value } }) => {
    this.setState({ minuteField: value }, () => {
      // biome-ignore lint/suspicious/noGlobalIsNan: legacy code
      if (!isNaN(value) && value >= 1 && value <= 60) {
        const newValue = getValue(this.state);
        this.setState({ value: newValue }, () =>
          this.props.input.onChange({
            expression: newValue,
            timezone: this.state.timezone,
          }),
        );
      } else {
        this.setState({ value: '' }, () =>
          this.props.input.onChange({
            expression: '',
            timezone: this.state.timezone,
          }),
        );
      }
    });
  };

  handleTimezoneChange = ({ value }) => {
    this.setState({ timezone: value }, () => {
      this.props.input.onChange({
        expression: this.state.value,
        timezone: this.state.timezone,
      });
    });
  };

  handlePeriodChange = handleEvent((data) => {
    const period = data.value;
    const { minutes } = this.state;
    const {
      input: { onChange },
    } = this.props;
    this.setState(
      {
        period,
        value:
          period !== 'minute' && this.isInvalidMinutes(minutes)
            ? ''
            : getValue({ ...this.state, period }),
      },
      () => {
        if (period !== 'minute' && this.isInvalidMinutes(this.state.minutes)) {
          onChange({ expression: '', timezone: this.state.timezone });
        } else {
          onChange({
            expression: this.state.value,
            timezone: this.state.timezone,
          });
        }
      },
    );
  });

  handleMonthsChange = (data = []) => {
    const oldValue = this.state.months;
    const saveValue = this.getSaveValue(oldValue, data);
    this.setState({ months: saveValue }, this.updateInputValue);
  };

  handleDaysChange = (data = []) => {
    const oldValue = this.state.days;
    const saveValue = this.getSaveValue(oldValue, data);
    this.setState({ days: saveValue }, this.updateInputValue);
  };

  handleDaysOfWeekChange = (data = []) => {
    const oldValue = this.state.daysOfWeek;
    const saveValue = this.getSaveValue(oldValue, data);
    this.setState({ daysOfWeek: saveValue }, this.updateInputValue);
  };

  handleHoursChange = (data = []) => {
    const oldValue = this.state.hours;
    const saveValue = this.getSaveValue(oldValue, data);
    this.setState({ hours: saveValue }, this.updateInputValue);
  };

  handleMinutesChange = (data = []) => {
    const oldValue = this.state.minutes;
    const saveValue = this.getSaveValue(oldValue, data);
    this.setState({ minutes: saveValue }, () => {
      if (Array.isArray(data) && data.length) {
        const newValue = getValue(this.state);
        this.setState({ value: newValue }, () =>
          this.props.input.onChange({
            expression: newValue,
            timezone: this.state.timezone,
          }),
        );
      } else {
        this.setState({ value: '' }, () =>
          this.props.input.onChange({
            expression: '',
            timezone: this.state.timezone,
          }),
        );
      }
    });
  };

  handleReset = handleEvent(() => {
    this.setState(
      {
        months: null,
        days: null,
        daysOfWeek: null,
        hours: null,
        minutes: null,
        minuteField: 5,
        value: '',
        timezone: 'UTC',
      },
      () => this.props.input.onChange({ expression: '', timezone: 'UTC' }),
    );
  });

  isInvalidMinutes = (minutes) =>
    minutes === '' || isNullable(minutes) || minutes === '*';

  render() {
    const {
      msg,
      disabled,
      meta: { touched, error },
    } = this.props;
    const { period } = this.state;
    const periodOptions = this.getPeriodOptions();

    return (
      <div>
        <div className='schedule-field flex flex-wrap'>
          <div className='schedule-field__group'>
            <span className='mr-2'>{msg.every}</span>
            <Select
              autoFocus
              autosize
              className='period-select'
              classNamePrefix='react-select'
              hideSelectedOptions={false}
              isClearable={false}
              isDisabled={disabled}
              isSearchable={false}
              onChange={this.handlePeriodChange}
              options={periodOptions}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 30,
                  borderRadius: 0,
                }),
                dropdownIndicator: (base) => ({ ...base, padding: '2px 8px' }),
                valueContainer: (base) => ({ ...base, padding: '0 6px' }),
              }}
              value={periodOptions.filter((option) => option.value === period)}
            />
          </div>
          {period === 'minute' ? this.getMinute() : null}
          {period === 'hour' ? this.getHour() : null}
          {period === 'day' ? this.getDay() : null}
          {period === 'week' ? this.getWeek() : null}
          {period === 'month' ? this.getMonth() : null}
          {period === 'year' ? this.getYear() : null}
        </div>
        <div className='flex'>
          {this.getTimezone()}
          <Button
            className='ml-2'
            disabled={disabled}
            icon='fa-xmark'
            onClick={this.handleReset}
            variant='tertiary'
            type='button'
          />
        </div>
        {touched && error && (
          <FormErrorMessage>{this.getErrorMessage()}</FormErrorMessage>
        )}
      </div>
    );
  }
}
